// Backward-scan helpers for `detectComparisonValueContext` — pure
// string/token scanners (no grammar dependency) that walk left from the
// cursor to the comparison-operator boundary and the LHS column reference.
// Split out to keep the detector ≤200 lines; guarded by its test file.

// Multi-char ops first so `<=`/`>=`/`<>`/`!=` aren't matched as `<`/`>`/`!`.
export const COMPARISON_OPERATORS: readonly string[] = [
  '<=', '>=', '<>', '!=', '=', '<', '>',
];

const IDENT_CHAR = /[A-Za-z0-9_"$`[\]]/;
const IDENT_START = /[A-Za-z_"`[]/;

/**
 * Step back over a T-SQL national-literal prefix (`N'…'`) when `i` sits on
 * its `N`. Bounded to a single letter so a preceding identifier
 * (`column'…'`) is not over-consumed — the caller's boundary scan would
 * still fail if the `N` is part of a longer word. Returns `i` unchanged when
 * there is no prefix.
 */
function skipNationalLiteralPrefix(text: string, i: number): number {
  if (i < 0) return i;
  const ch = text[i];
  if (ch !== 'N' && ch !== 'n') return i;
  if (i > 0 && /[A-Za-z0-9_]/.test(text[i - 1]!)) return i;
  return i - 1;
}

/**
 * From `start` (char just left of the cursor), skip back over an optional
 * in-progress string operand, leading `IN ( … )` list items, surrounding
 * whitespace, and a single opening `(`. Returns the index of the last
 * non-skipped char (operator tail / `IN` / `BETWEEN` end), or -1.
 */
export function skipValueOperand(text: string, start: number): number {
  let i = start;
  // (a) Consume the cursor's in-progress operand: an unclosed `'<partial>`
  //     literal (step past its opening quote), or empty / a bare partial
  //     token (`= |`, `IN (|`, `'a', |`).
  let sawWs = false;
  while (i >= 0 && /\s/.test(text[i]!)) {
    i -= 1;
    sawWs = true;
  }
  if (i >= 0 && text[i] === "'") {
    // The in-progress operand's body is to the RIGHT of the cursor; this
    // `'` is its lone opening quote. Step past it, then past an optional
    // T-SQL `N` literal prefix.
    i = skipNationalLiteralPrefix(text, i - 1);
  } else if (!sawWs) {
    // Bare partial token directly under the cursor (no quote) — skip it so
    // `IN (open|` ≡ `IN (|`. Only when NOT ws-separated: a ws gap means the
    // operand is empty (`AND |`, `= |`) and the left token is the
    // operator/keyword boundary — consuming `AND` here broke `BETWEEN x AND |`.
    // `%` rides along with the identifier chars because a LIKE pattern's body
    // is a partial too: `LIKE '%|` must reach its opening quote, or the
    // boundary lands on the `%` — neither an operator tail nor a keyword — and
    // the collectors go silent inside every wildcard pattern (mysql coverage
    // script.16 §3). Outside a literal it changes nothing: `a %|` still walks
    // on to `a`, which is no operator boundary either.
    while (i >= 0 && /[A-Za-z0-9_%]/.test(text[i]!)) i -= 1;
    // The partial may be the body of an in-progress string literal whose
    // opening quote has no match yet (`= 'op|`, `IN ('a', 'op|`): step past
    // that opening quote too, otherwise the boundary scan stops on the quote
    // instead of reaching the comparison operator and no context is found.
    // The T-SQL `N` prefix (`= N'op|`) must go with it for the same reason —
    // without it the boundary lands on the `N`, which is neither an operator
    // tail nor `IN`/`BETWEEN`, and the whole enum-value / value-slot
    // collector goes silent the moment the user types a character inside an
    // `N'…'` literal (mssql coverage script.21 §2–§4, script.22 §2).
    if (i >= 0 && text[i] === "'") {
      i = skipNationalLiteralPrefix(text, i - 1);
    } else {
      // Multi-word in-progress literal: `= 'MPEG a|`, `IN ('x', 'Purchased A|`.
      // The partial under the caret is only the LAST word of the body; the
      // opening quote sits further left behind other words and spaces. Walk
      // back over plain value characters on this line and, when that walk
      // ends on a quote that opens a literal (nothing but an operator, `(`,
      // `,` or a keyword boundary before it), step past it like a single-word
      // body. Without this every enum value containing a space went silent
      // the moment its second word was started (sqlite coverage script.24).
      const quoteIdx = findOpeningQuoteOfMultiWordBody(text, i);
      if (quoteIdx !== -1) i = skipNationalLiteralPrefix(text, quoteIdx - 1);
    }
  }
  // (b) Loop over preceding closed list items + whitespace to the `(`/operator.
  for (;;) {
    while (i >= 0 && /\s/.test(text[i]!)) i -= 1;
    if (i < 0) return -1;
    if (i >= 0 && text[i] === ',') {
      // List continuation inside `IN ( … )` — skip the preceding item.
      i -= 1;
      while (i >= 0 && /\s/.test(text[i]!)) i -= 1;
      if (i >= 0 && text[i] === "'") {
        // Closed string list item — scan back to its opening quote.
        i -= 1;
        while (i >= 0) {
          if (text[i] === "'") {
            if (text[i - 1] === "'") {
              i -= 2;
              continue;
            }
            i -= 1;
            break;
          }
          if (text[i] === '\n') break;
          i -= 1;
        }
        // Step past optional T-SQL N-prefix on the closed list item.
        i = skipNationalLiteralPrefix(text, i);
      } else {
        // Numeric / identifier list item.
        while (i >= 0 && /[A-Za-z0-9_.]/.test(text[i]!)) i -= 1;
      }
      continue;
    }
    if (i >= 0 && text[i] === '(') {
      // Open paren of `IN ( … )` — step over it; the `IN` keyword sits to
      // its left.
      i -= 1;
      while (i >= 0 && /\s/.test(text[i]!)) i -= 1;
      return i;
    }
    return i;
  }
}

/**
 * When the boundary forms the head of an `IN`/`BETWEEN` comparison, return
 * the offset *just past the LHS column reference*; `undefined` otherwise.
 * Handles `<col> IN (` (i at `N` of `IN`), `<col> BETWEEN <lo>` (i at `N` of
 * `BETWEEN`, cursor at low bound), and `<col> BETWEEN <lo> AND` (i at `D` of
 * `AND`, cursor at high bound). Each form may be `NOT`-negated; the returned
 * offset sits *before* an optional preceding `NOT` so the caller reads the
 * column, not `NOT`.
 */
export function scanInOrBetweenHead(
  text: string,
  i: number,
): number | undefined {
  const kw = readKeywordBefore(text, i + 1);
  if (kw === 'IN' || kw === 'BETWEEN') {
    return stripOptionalNot(text, i + 1 - kw.length);
  }
  if (kw === 'AND') {
    // `<col> BETWEEN <lo> AND |` — walk back over the low-bound token then
    // expect `BETWEEN`.
    let j = i + 1 - kw.length;
    while (j >= 0 && /\s/.test(text[j - 1]!)) j -= 1;
    j = skipOneBoundToken(text, j - 1) + 1;
    while (j >= 0 && /\s/.test(text[j - 1]!)) j -= 1;
    const between = readKeywordBefore(text, j);
    if (between === 'BETWEEN') {
      return stripOptionalNot(text, j - between.length);
    }
    return undefined;
  }
  return undefined;
}

/**
 * Step `end` (offset just past the LHS column of an `IN`/`BETWEEN` form) back
 * over an optional `NOT` so `<col> NOT IN (` resolves to `<col>`, not `NOT`.
 */
function stripOptionalNot(text: string, end: number): number {
  let i = end;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  const wordEnd = i;
  while (i > 0 && /[A-Za-z]/.test(text[i - 1]!)) i -= 1;
  return text.slice(i, wordEnd).toUpperCase() === 'NOT' ? i : end;
}

/** Skip a single simple bound token (number, identifier, or quoted string). */
function skipOneBoundToken(text: string, end: number): number {
  let i = end;
  while (i >= 0 && /\s/.test(text[i]!)) i -= 1;
  if (i >= 0 && text[i] === "'") {
    i -= 1;
    while (i >= 0 && text[i] !== "'") i -= 1;
    return i - 1;
  }
  while (i >= 0 && /[A-Za-z0-9_."`[\]$]/.test(text[i]!)) i -= 1;
  return i;
}

/**
 * Read an alphabetic keyword whose last char is at index `end - 1`. Returns
 * the upper-cased word, or '' when no alphabetic run ends there.
 */
export function readKeywordBefore(text: string, end: number): string {
  let i = end;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  const wordEnd = i;
  while (i > 0 && /[A-Za-z]/.test(text[i - 1]!)) i -= 1;
  return text.slice(i, wordEnd).toUpperCase();
}

/**
 * When the cursor sits inside an in-progress single-quoted string *value
 * operand* (`= '|`, `= 'op|`, `IN ('a', 'op|`), return the offset just past
 * that operand's opening quote (its body start). `undefined` for empty / bare
 * operands (`= |`, `IN (|`) so the caller can fall back to a quoted literal.
 *
 * Left-bounded by `skipValueOperand` so a *closed* string earlier in the
 * statement (`x = 'a' OR y = |`) is never mistaken for the cursor's operand.
 * `''` is treated as an escaped quote (SQL doubling), matching the same
 * convention as `findJsonPathStringPosition`.
 */
export function findValueStringBodyStart(
  text: string,
  offset: number,
): number | undefined {
  const bound = skipValueOperand(text, offset - 1);
  let i = offset - 1;
  while (i > bound) {
    const ch = text[i]!;
    if (ch === '\n') return undefined;
    if (ch === "'") {
      if (text[i - 1] === "'") {
        i -= 2; // escaped '' — part of the body, keep scanning left
        continue;
      }
      return i + 1; // opening quote of the in-progress operand
    }
    i -= 1;
  }
  return undefined;
}

/**
 * Index of the `'` that opens the multi-word literal body ending at `end`
 * (inclusive, the char left of an already-consumed partial), or -1. The body
 * may hold word characters, spaces and common value punctuation but no quote
 * or newline; a `''` pair inside it is an escaped quote. The quote must be an
 * OPENING one: what precedes it (after optional `N` and whitespace) is an
 * operator character, `(`, `,`, or a word boundary — never a word character
 * that would make it a CLOSING quote of an earlier literal.
 */
function findOpeningQuoteOfMultiWordBody(text: string, end: number): number {
  let i = end;
  let sawSpace = false;
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === '\n') return -1;
    if (ch === "'") {
      if (i > 0 && text[i - 1] === "'") {
        i -= 2;
        continue;
      }
      break;
    }
    if (ch === ' ') sawSpace = true;
    else if (!/[\w%.\-/:+@#]/.test(ch)) return -1;
    i -= 1;
  }
  if (i < 0 || !sawSpace) return -1;
  let j = i - 1;
  if (j >= 0 && /[Nn]/.test(text[j]!) && !/[\w$]/.test(text[j - 1] ?? '')) j -= 1;
  while (j >= 0 && /\s/.test(text[j]!)) j -= 1;
  if (j < 0) return i;
  return /[=<>!(,]/.test(text[j]!) || /\w/.test(text[j]!) ? i : -1;
}

/**
 * True when `text` ends with `op` at `end` (exclusive). Multi-char ops
 * (`<=`/`>=`/`<>`/`!=`) are tried first by the caller's ordering.
 */
export function endsWithAt(text: string, end: number, op: string): boolean {
  if (end - op.length < 0) return false;
  return text.slice(end - op.length, end) === op;
}

/**
 * Read a 1–N-part dotted identifier whose last char is just before `end`
 * (whitespace-tolerant): `status`, `o.status`, `s.t.status`. `undefined` when
 * none is visible — incl. a function-call tail `)` (`UPPER(status) = '|'`).
 */
export function readDottedIdentifierBefore(
  text: string,
  end: number,
): string | undefined {
  let i = end;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  if (i <= 0) return undefined;
  // A closing paren or non-identifier tail means this is not a bare column
  // reference (function result / arithmetic / sub-expression).
  const tailCh = text[i - 1]!;
  if (!IDENT_CHAR.test(tailCh)) return undefined;
  const partsEnd = i;
  for (;;) {
    const segEnd = i;
    while (i > 0 && IDENT_CHAR.test(text[i - 1]!)) i -= 1;
    if (i === segEnd) break;
    let k = i;
    while (k > 0 && /\s/.test(text[k - 1]!)) k -= 1;
    if (text[k - 1] === '.') {
      i = k - 1;
      while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
      continue;
    }
    break;
  }
  const raw = text.slice(i, partsEnd).trim();
  if (raw.length === 0) return undefined;
  // The first identifier segment must start with an identifier-start char
  // (rejects a leading number like `1status`, an arithmetic remnant).
  const firstSeg = raw.split('.')[0]!.trim();
  if (firstSeg.length === 0 || !IDENT_START.test(firstSeg[0]!)) {
    if (!/[A-Za-z_]/.test(firstSeg[0] ?? '')) return undefined;
  }
  return raw;
}
